import {
    GetAccountsResponse,
    GetApplicationResponse,
    GetFormalOffersResponse
} from '@iwoca/lapi-client/edge';
import { Account, Application, FormalOffer } from './Dashboard.types';

export const DECLINED_STATUS = 'declined' as const;
export const DEFERRED_STATUS = 'deferred' as const;
export const APPROVED_STATUS = 'approved' as const;
export const OFFERED_APPLICATION_STATUS = 'offered' as const;
export const PENDING_STATUS = 'pending' as const;
export const OUTSTANDING_STATUS = 'outstanding' as const;
export const APPROVAL_EXPIRED_STATUS = 'approval_expired' as const;
export const APPROVAL_INVALID_STATUS = 'approval_invalid' as const;
export const UNKNOWN_STATUS = 'unknown' as const;
export const APPLY_REVENUE_BASED_LOAN_STATUS = 'applyRblLoan' as const;

export const getRBLAccount = (
    accountsResponse: GetAccountsResponse
): Account | null => {
    for (const account of accountsResponse.data!.accounts) {
        if (
            account.product_type === 'merchant_cash_advance' &&
            account.account_id
        ) {
            return account;
        }
    }
    return null;
};

export const getRBLApplication = (
    applicationResponse: GetApplicationResponse
): Application | null => {
    if (!applicationResponse.data) return null;
    if (!applicationResponse.data.requested_products) return null;

    for (const requestedProduct of applicationResponse.data
        .requested_products) {
        if (
            requestedProduct.suggested_product &&
            requestedProduct.suggested_product.product_type ===
                'merchant_cash_advance'
        ) {
            return applicationResponse.data!;
        } else {
            return null;
        }
    }
    return null;
};

export const getRBLFormalOffer = (
    formalOffersResponse: GetFormalOffersResponse
): FormalOffer | null => {
    if (!formalOffersResponse.data) {
        return null;
    } else {
        for (const formalOffer of formalOffersResponse.data.formal_offers) {
            if (formalOffer.product_type === 'merchant_cash_advance') {
                return formalOffer;
            } else {
                continue;
            }
        }
    }
    return null;
};

export const getRBLApplicationStatus = (
    rblApplication: Application
):
    | typeof PENDING_STATUS
    | typeof DECLINED_STATUS
    | typeof DEFERRED_STATUS
    | typeof OFFERED_APPLICATION_STATUS
    | typeof UNKNOWN_STATUS => {
    if (isOpenApplication(rblApplication)) {
        return PENDING_STATUS;
    } else if (isDeferredApplication(rblApplication)) {
        return DEFERRED_STATUS;
    } else if (isDeclinedApplication(rblApplication)) {
        return DECLINED_STATUS;
    } else if (isOfferedApplication(rblApplication)) {
        return OFFERED_APPLICATION_STATUS;
    } else {
        return UNKNOWN_STATUS;
    }
};

export const getRBLFormalOfferStatus = (
    rblFormalOffer: FormalOffer
):
    | typeof APPROVAL_EXPIRED_STATUS
    | typeof APPROVAL_INVALID_STATUS
    | typeof APPROVED_STATUS => {
    if (!isValidOffer(rblFormalOffer)) {
        if (isTimedOutOffer(rblFormalOffer)) {
            return APPROVAL_EXPIRED_STATUS;
        } else {
            return APPROVAL_INVALID_STATUS;
        }
    } else {
        return APPROVED_STATUS;
    }
};

const isValidOffer = (formalOffer: FormalOffer): boolean => {
    return formalOffer.is_valid!;
};
const isTimedOutOffer = (formalOffer: FormalOffer): boolean => {
    return formalOffer.is_timed_out!;
};

const isOpenApplication = (application: Application): boolean => {
    return application.status === 'open';
};
const isDeferredApplication = (application: Application): boolean => {
    return application.status === 'deferred';
};
const isDeclinedApplication = (application: Application): boolean => {
    return application.status === 'declined';
};
const isOfferedApplication = (application: Application): boolean => {
    return application.status === 'offered';
};
